import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import api from '../api/index.js';

const AuthContext = createContext(null);

const USER_KEY = 'ams_user';
const LEGACY_KEY = 'user'; // legacy key read by 36 components still being migrated

function loadStoredUser() {
  try {
    // Prefer the canonical key; fall back to legacy key for sessions started
    // before the AuthContext migration (avoids logout-on-refresh for active users)
    return (
      JSON.parse(localStorage.getItem(USER_KEY)) ||
      JSON.parse(localStorage.getItem(LEGACY_KEY))
    );
  } catch {
    return null;
  }
}

// Set the auth header SYNCHRONOUSLY at module load — before any component renders
// or runs its mount effect. React runs child effects BEFORE parent effects, so
// without this a raw-`axios` call in a child's mount effect (NotificationCenter,
// TeamCalendar, AthleteHome, …) fires on first paint/refresh before the
// AuthProvider effect below sets the header → 401 → empty data. This runs at
// import time (AuthContext is in the boot graph), killing that race app-wide.
(() => {
  try {
    const u = JSON.parse(localStorage.getItem(USER_KEY)) || JSON.parse(localStorage.getItem(LEGACY_KEY));
    if (u?.token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${u.token}`;
      api.defaults.headers.common['Authorization'] = `Bearer ${u.token}`;
    }
  } catch { /* no stored session yet */ }
})();

function persistUser(userData) {
  if (userData) {
    const s = JSON.stringify(userData);
    localStorage.setItem(USER_KEY, s);
    localStorage.setItem(LEGACY_KEY, s); // dual-write until all 36 components migrated
  } else {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LEGACY_KEY);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(loadStoredUser);
  const [loading, setLoading] = useState(false);

  // Keep the auth header in sync with the token on BOTH the shared `api` instance
  // and the global `axios` default. Many older components (TeamCalendar, etc.) call
  // raw `axios` directly; without this they'd fire unauthenticated requests and 401
  // (empty athlete/class pickers, "Failed to save event", …).
  useEffect(() => {
    if (user?.token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${user.token}`;
      axios.defaults.headers.common['Authorization'] = `Bearer ${user.token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [user?.token]);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post('/api/auth/login', { email, password });
      if (!data.success) throw new Error(data.message || 'Login failed');
      const userData = { ...data.user, token: data.token };
      persistUser(userData);
      setUser(userData);
      return userData;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    persistUser(null);
    setUser(null);
  }, []);

  // updateUser: used by Login.js (which does its own fetch) to sync context
  const updateUser = useCallback((patch) => {
    setUser(prev => {
      const updated = prev ? { ...prev, ...patch } : patch;
      persistUser(updated);
      return updated;
    });
  }, []);

  const isRMT = user?.role === 'superadmin' || user?.role === 'staff';

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser, isRMT }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
